import {useMemo} from 'react';

import {useI18n} from '@udemy/i18n';
import {useCoursesForCartUpsellByIds} from '@udemy/react-certification-preparation';
import {ShoppingItem as UdemyShoppingItem} from '@udemy/shopping';

import {useBundleCoursesForVoucher} from 'src/hooks/use-bundle-courses-for-voucher';
import {useCoursePrice} from 'src/hooks/use-course-price';

import {
    buildShoppingItemFromGqlCourse,
    isEffectivelyFree,
} from '../certification-upsell-shopping-item-builders';

export interface UseCertificationVoucherUpsellParams {
    offerId: string;
    cartCourseIds?: Set<number>;
}

export interface UseCertificationVoucherUpsellResult {
    courseItems: UdemyShoppingItem[];
    hasContent: boolean;
    isLoading: boolean;
}

export function useCertificationVoucherUpsell({
    offerId,
    cartCourseIds,
}: UseCertificationVoucherUpsellParams): UseCertificationVoucherUpsellResult {
    const {gettext} = useI18n();

    const {courseIdsToUpsell, isLoading: bundleLoading} = useBundleCoursesForVoucher(
        offerId,
        cartCourseIds,
    );

    const {courses, isLoading: coursesLoading} = useCoursesForCartUpsellByIds(courseIdsToUpsell);

    // useCoursePrice must be called unconditionally (hooks rules) — bundles have at most 2 courses.
    const {price: coursePrice0, isLoading: coursePriceLoading0} = useCoursePrice(
        courseIdsToUpsell[0] ?? null,
    );
    const {price: coursePrice1, isLoading: coursePriceLoading1} = useCoursePrice(
        courseIdsToUpsell[1] ?? null,
    );

    const courseItems = useMemo(() => {
        if (coursesLoading) return [];

        const courseById = new Map(
            courses
                .filter((course): course is NonNullable<(typeof courses)[number]> => course != null)
                .map((course) => [parseInt(course.id, 10), course] as const),
        );

        const prices = [coursePrice0, coursePrice1];
        const pricesLoading = [coursePriceLoading0, coursePriceLoading1];

        return courseIdsToUpsell
            .map((courseId, index) => {
                const course = courseById.get(courseId);
                if (!course || course.isEnrolled) return null;
                const candidate = buildShoppingItemFromGqlCourse(course, prices[index], gettext);
                if (!candidate) return null;
                if (isEffectivelyFree(candidate, pricesLoading[index])) return null;
                return candidate;
            })
            .filter((course) => course != null);
    }, [
        courseIdsToUpsell,
        courses,
        coursesLoading,
        coursePrice0,
        coursePrice1,
        coursePriceLoading0,
        coursePriceLoading1,
        gettext,
    ]);

    // `useCoursesForCartUpsellByIds([])` reports isLoading:true even when the
    // query is skipped (enabled:false). Guard the same way prices are gated so
    // vouchers with no upsell candidates settle immediately instead of hanging.
    const isLoading =
        bundleLoading ||
        (courseIdsToUpsell.length > 0 && coursesLoading) ||
        (courseIdsToUpsell[0] != null && coursePriceLoading0) ||
        (courseIdsToUpsell[1] != null && coursePriceLoading1);
    const hasContent = !isLoading && courseItems.length > 0;

    return {courseItems, hasContent, isLoading};
}
